import { useState, useEffect, useContext, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext, API } from '@/App';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { ArrowLeft, MapPin, Calendar, User, Share2, Camera } from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';
import VideoPlayer from '@/components/VideoPlayer';
import SocialConnect from '@/components/SocialConnect';
import GamificationStats from '@/components/GamificationStats';
import SafetyFeatures from '@/components/SafetyFeatures';

const Profile = () => {
  const { user, logout, refreshUser } = useContext(AuthContext);
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const [checkins, setCheckins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [profilePicture, setProfilePicture] = useState(user?.profile_picture_url || null);
  const [currentUser, setCurrentUser] = useState(user);
  const [friends, setFriends] = useState([]);
  const [activeCheckin, setActiveCheckin] = useState(null);
  const [showChangeHint, setShowChangeHint] = useState(false);

  useEffect(() => {
    fetchCheckins();
    fetchUserData();
    fetchFriends();
  }, []);

  const fetchUserData = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`);
      setCurrentUser(response.data);
      setProfilePicture(response.data.profile_picture_url || null);
      // Also update the global user state
      if (refreshUser) refreshUser();
    } catch (e) {
      console.error(e);
    }
  };

  const fetchFriends = async () => {
    try {
      const response = await axios.get(`${API}/friends`);
      setFriends(response.data);
    } catch (e) {
      console.error(e);
    }
  };

  const fetchCheckins = async () => {
    try {
      const response = await axios.get(`${API}/checkins/my`);
      setCheckins(response.data);
      // Find active check-in
      const active = response.data.find(c => !c.checked_out);
      setActiveCheckin(active || null);
    } catch (e) {
      console.error(e);
      toast.error('Failed to load check-ins');
    } finally {
      setLoading(false);
    }
  };

  const handleCheckout = async (checkinId) => {
    try {
      await axios.post(`${API}/checkins/${checkinId}/checkout`);
      toast.success('Checked out successfully');
      fetchCheckins();
    } catch (e) {
      console.error(e);
      toast.error('Failed to check out');
    }
  };

  const handleProfilePictureUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('picture', file);

      const response = await axios.post(`${API}/auth/upload-profile-picture`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      setProfilePicture(response.data.profile_picture_url);
      // Also refresh user data globally
      if (refreshUser) {
        await refreshUser();
      }
      toast.success('Profile picture updated!');
    } catch (e) {
      console.error(e);
      toast.error('Failed to upload profile picture');
    } finally {
      setUploading(false);
    }
  };

  const handlePictureClick = () => {
    fileInputRef.current?.click();
  };

  const handlePictureLongPress = () => {
    setShowChangeHint(true);
    fileInputRef.current?.click();
  };

  const handleShareCheckin = async (checkinId) => {
    const socialAccounts = currentUser?.social_accounts || [];
    if (socialAccounts.length === 0) {
      toast.error('Connect social accounts first to share!');
      return;
    }

    const platforms = socialAccounts.map(acc => acc.provider);
    
    try {
      const response = await axios.post(`${API}/checkins/${checkinId}/share`, { platforms });
      const bonusDiscount = response.data.bonus_discount_percent;
      
      if (bonusDiscount > 0) {
        toast.success(`🎉 Shared! You earned ${bonusDiscount}% bonus discount!`);
      } else {
        toast.success('Shared to social media!');
      }
    } catch (e) {
      console.error(e);
      toast.error('Failed to share');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-900">
        <div className="text-white text-xl">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900">
      {/* Header */}
      <header className="glass sticky top-0 z-50 border-b border-slate-700">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/')}
              className="text-white hover:bg-white/10 mr-4"
              data-testid="back-to-home-btn"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
            <h1 className="text-xl font-bold text-white" style={{ fontFamily: 'Space Grotesk' }}>
              My Profile
            </h1>
          </div>
          <img 
            src="/nightbuzz-logo.png" 
            alt="NightBuZz" 
            className="h-10 rounded-lg cursor-pointer"
            onClick={() => navigate('/home')}
          />
        </div>
      </header>

      <div className="container mx-auto px-4 py-8 max-w-4xl">
        {/* User Info */}
        <Card className="glass p-6 border border-slate-700 mb-8 fade-in">
          <div className="flex items-start gap-6">
            <div className="relative group">
              {/* Hidden file input */}
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleProfilePictureUpload}
                className="hidden"
                disabled={uploading}
              />
              
              {/* Clickable profile picture container */}
              <div 
                className="relative cursor-pointer"
                onClick={handlePictureClick}
                onContextMenu={(e) => {
                  e.preventDefault();
                  handlePictureLongPress();
                }}
                onTouchStart={(e) => {
                  // Long press for mobile
                  const timer = setTimeout(() => {
                    handlePictureLongPress();
                  }, 500);
                  e.target.longPressTimer = timer;
                }}
                onTouchEnd={(e) => {
                  clearTimeout(e.target.longPressTimer);
                }}
              >
                {profilePicture || user.profile_picture_url ? (
                  <img
                    src={`${process.env.REACT_APP_BACKEND_URL}${profilePicture || user.profile_picture_url}`}
                    alt={user.username}
                    className="w-24 h-24 rounded-full object-cover border-2 border-purple-500 group-hover:border-indigo-400 transition-all"
                  />
                ) : (
                  <div className="w-24 h-24 bg-purple-600 rounded-full flex items-center justify-center border-2 border-purple-500 group-hover:border-indigo-400 transition-all">
                    <User className="w-12 h-12 text-white" />
                  </div>
                )}
                
                {/* Hover overlay */}
                <div className="absolute inset-0 rounded-full bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                  <div className="text-center">
                    <Camera className="w-6 h-6 text-white mx-auto" />
                    <span className="text-xs text-white">Change</span>
                  </div>
                </div>
                
                {/* Uploading indicator */}
                {uploading && (
                  <div className="absolute inset-0 rounded-full bg-black/70 flex items-center justify-center">
                    <div className="w-6 h-6 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  </div>
                )}
              </div>
              
              {/* Camera icon badge (still visible but clicking anywhere works) */}
              <div className="absolute bottom-0 right-0 bg-indigo-600 rounded-full p-2 pointer-events-none">
                <Camera className="w-4 h-4 text-white" />
              </div>
            </div>
            <div className="flex-1">
              <h2 className="text-2xl font-bold text-white mb-2" data-testid="profile-username">{user.username}</h2>
              <p className="text-slate-300 mb-3" data-testid="profile-email">{user.email}</p>
              <div className="flex gap-2">
                <Badge className="bg-purple-500/20 text-purple-400" data-testid="profile-age">
                  {user.age} years old
                </Badge>
                <Badge className="bg-purple-500/20 text-purple-400" data-testid="profile-gender">
                  {user.gender}
                </Badge>
              </div>
            </div>
          </div>
        </Card>

        {/* Social Media Connect */}
        <SocialConnect user={currentUser} onUpdate={fetchUserData} />

        {/* Gamification Stats */}
        <GamificationStats />

        {/* Safety Features */}
        <SafetyFeatures currentCheckin={activeCheckin} friends={friends} />

        {/* Check-in History */}
        <Card className="glass p-6 border border-slate-700 fade-in">
          <h3 className="text-xl font-semibold text-white mb-6 flex items-center gap-2">
            <MapPin className="w-5 h-5 text-purple-400" />
            Check-in History
          </h3>
          
          {checkins.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-slate-400 mb-4">No check-ins yet</p>
              <Button
                onClick={() => navigate('/')}
                className="bg-purple-600 hover:bg-purple-700"
              >
                Find Bars
              </Button>
            </div>
          ) : (
            <div className="space-y-4">
              {checkins.map(checkin => (
                <div
                  key={checkin.id}
                  className="p-4 bg-slate-800/50 rounded-lg border border-slate-700 flex items-center justify-between"
                  data-testid={`checkin-item-${checkin.id}`}
                >
                  <div className="flex-1">
                    <h4 className="font-semibold text-white mb-1">
                      {checkin.bar?.name || 'Unknown Bar'}
                    </h4>
                    <p className="text-sm text-slate-400 flex items-center gap-2 mb-2">
                      <Calendar className="w-3 h-3" />
                      {format(new Date(checkin.timestamp), 'PPp')}
                    </p>
                    {checkin.tagged_friends && checkin.tagged_friends.length > 0 && (
                      <p className="text-sm text-slate-300 mb-2">
                        Tagged {checkin.tagged_friends.length} friend(s)
                      </p>
                    )}
                    {checkin.video_url && (
                      <div className="mt-3">
                        <VideoPlayer
                          videoUrl={`${process.env.REACT_APP_BACKEND_URL}${checkin.video_url}`}
                          videoTags={checkin.video_tags || []}
                          className="w-full max-w-md rounded-lg"
                        />
                        {checkin.video_tags && checkin.video_tags.length > 0 && (
                          <div className="mt-2 flex flex-wrap gap-2">
                            {checkin.video_tags.map((tag, idx) => (
                              <span key={idx} className="text-xs bg-purple-600/20 text-purple-300 px-2 py-1 rounded-full">
                                @{tag.friend_username}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                  <div className="flex flex-col gap-2">
                    {!checkin.checked_out && (
                      <Button
                        size="sm"
                        onClick={() => handleShareCheckin(checkin.id)}
                        className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700"
                        data-testid={`share-checkin-btn-${checkin.id}`}
                      >
                        <Share2 className="w-3 h-3 mr-1" />
                        Share
                      </Button>
                    )}
                    {checkin.checked_out ? (
                      <Badge className="bg-slate-700 text-slate-300">Checked Out</Badge>
                    ) : (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleCheckout(checkin.id)}
                        className="border-purple-500 text-purple-400 hover:bg-purple-500/10"
                        data-testid={`checkout-btn-${checkin.id}`}
                      >
                        Check Out
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>
      </div>
    </div>
  );
};

export default Profile;